import { z } from 'zod';
const optionalNumber = z.number().optional();
const optionalBoolean = z.boolean().optional();
/**
 * A reusable schema for arrays of numeric identifiers.
 * Safely coerces legacy comma-separated strings (e.g., "123,456") into arrays of numbers.
 * Also coerces arrays of strings (e.g., ["123", "456"]) into arrays of numbers.
 * Defaults to an empty array if the key is missing or blank.
 */
const coerceIdArray = z.preprocess((val) => {
    if (typeof val === 'string') {
        // If it's an empty string, return an empty array
        if (!val.trim())
            return [];
        // Otherwise, split by comma into an array of strings
        return val.split(',');
    }
    return val;
}, z.array(z.coerce.number()).default([]));
/**
 * Coerces a legacy "field,order" string into a strictly typed Sort object.
 */
const coerceSortObject = z.preprocess((val) => {
    if (typeof val === 'string') {
        const [field, order] = val.split(',');
        return {
            field: field === null || field === void 0 ? void 0 : field.trim(),
            order: order === null || order === void 0 ? void 0 : order.trim().toLowerCase()
        };
    }
    return val;
}, z
    .object({
    field: z.string().min(1, 'Sort field is required'),
    order: z.enum(['asc', 'desc'], {
        required_error: "Sort order is missing. If passing a string, ensure it has a comma (e.g., 'name,asc').",
        invalid_type_error: "Sort order must be strictly 'asc' or 'desc'."
    })
})
    .optional());
// ============================================================================
// SCHEMA & TYPES
// ============================================================================
export const TeamMemberFilterSchema = z.object({
    teamId: optionalNumber,
    teamIds: coerceIdArray,
    skip: optionalNumber,
    take: optionalNumber,
    sort: coerceSortObject,
    tags: z.string().optional(),
    active: optionalBoolean,
    pending: optionalBoolean,
    inactive: optionalBoolean,
    managers: optionalBoolean,
    selectedIds: coerceIdArray,
    selectedOnly: optionalBoolean,
    searchTeamNames: optionalBoolean
});
export const DEFAULT_TEAM_MEMBER_FILTER = {
    teamIds: [],
    selectedIds: []
};
/**
 * Safely parses, coerces, and validates raw filter payloads into a strict `TeamMemberFilter` object.
 * Acts as an Anti-Corruption Layer (ACL) to protect the backend from malformed frontend data.
 *
 * ⚠️ TEMPORARY MIGRATION WRAPPER ⚠️
 * This function is a temporary scaffold. It exists because several legacy JavaScript components
 * currently pass improperly formatted data (e.g., legacy `sort` strings like "name,asc" instead
 * of objects) to the new POST endpoint.
 *
 * TODO: (Migration) Once all files consuming the `queryTeamMembers` API have been migrated
 * to strict TypeScript, this wrapper should be DELETED. The application should rely directly
 * on TypeScript's static type checking at the call site.
 *
 * @param raw - The untyped/legacy filter payload (typically from component state or URL params).
 * @returns A strictly typed `TeamMemberFilter` object, falling back to safe defaults if validation fails.
 */
export const formatTeamMemberFilter = (raw) => {
    const data = typeof raw === 'object' && raw !== null ? raw : {};
    const result = TeamMemberFilterSchema.safeParse(data);
    if (!result.success) {
        const errorDetails = result.error.flatten();
        if (process.env.NODE_ENV !== 'production') {
            const errorTitle = 'TeamMemberFilter Validation Failed';
            console.groupCollapsed(`🚨 ${errorTitle}`);
            console.error('Stack Trace:');
            console.log('errorDetails.fieldErrors', errorDetails.fieldErrors);
            console.log('Original Input:', raw);
            console.groupEnd();
            const event = new CustomEvent('app-notification', {
                detail: {
                    title: 'TeamMemberFilter Validation Failed',
                    details: errorDetails.fieldErrors,
                    type: 'error'
                }
            });
            window.dispatchEvent(event);
        }
        else {
            console.error('Data integrity check failed. Using fallbacks.');
        }
        return DEFAULT_TEAM_MEMBER_FILTER;
    }
    return result.data;
};



//////////////////
// WEBPACK FOOTER
// ./src/state/modules/business/business/teamMemberFilter.ts
// module id = 2019
// module chunks = 0